import type { CSSProperties } from 'react';
import { cx } from './cx';
import './components.css';

export interface SkeletonProps {
  width?: number | string;
  height?: number | string;
  radius?: number | string;
  className?: string;
  style?: CSSProperties;
}

/**
 * Skeletons, never spinners: the page should show its own shape while it
 * loads, so nothing jumps when the data lands.
 */
export function Skeleton({ width = '100%', height = 14, radius, className, style }: SkeletonProps) {
  return (
    <div
      className={cx('k-skel', className)}
      aria-hidden="true"
      style={{ width, height, borderRadius: radius, ...style }}
    />
  );
}

/** A paragraph's worth of lines, last one short like real text. */
export function SkeletonText({ lines = 3, width = '100%' }: { lines?: number; width?: number | string }) {
  return (
    <div className="stack-sm" aria-hidden="true">
      {Array.from({ length: lines }, (_, i) => (
        <Skeleton key={i} className="k-skel--text" width={i === lines - 1 ? '62%' : width} />
      ))}
    </div>
  );
}

/** The shape of a <Stat>: label above, figure below. */
export function SkeletonStat({ size = 'md' }: { size?: 'sm' | 'md' | 'lg' | 'hero' }) {
  const h = size === 'hero' ? 64 : size === 'lg' ? 36 : size === 'md' ? 26 : 19;
  return (
    <div className="stack-sm" aria-hidden="true">
      <Skeleton className="k-skel--text" width={84} height={9} />
      <Skeleton width={size === 'hero' ? '68%' : 148} height={h} radius={8} />
    </div>
  );
}

/** Table rows, so a loading table keeps its columns. */
export function SkeletonRows({ rows = 6, cols = 4 }: { rows?: number; cols?: number }) {
  return (
    <div className="stack-sm" aria-hidden="true">
      {Array.from({ length: rows }, (_, r) => (
        <div key={r} className="row" style={{ gap: 12 }}>
          {Array.from({ length: cols }, (_, c) => (
            <Skeleton key={c} className="k-skel--text" height={11}
              style={{ flex: c === 0 ? '1 1 34%' : '1 1 0' }} />
          ))}
        </div>
      ))}
    </div>
  );
}
